import cn from "clsx";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Tooltip } from "react-migration/components/Tooltip";
import { Theme } from "react-migration/lib/theme/Theme";
import { RelatedOwners } from "./RelatedOwners";
import { useTitlesWithOwnerNamesQuery } from "../../hooks/useTitlesWithOwnerNamesQuery";
import { formatAddress } from "react-migration/domains/ownership/util/Formatters";
import { OwnershipIntersectableGeometry } from "react-migration/domains/ownership/apollo/useTitlesByLocationQuery";
import { Title } from "react-migration/domains/sites/typings/Title";
import { DataPanel } from "react-migration/components/DataPanel";
import { Divider } from "react-migration/components/Divider";
import { TitleWithOwnerNames } from "react-migration/domains/ownership/typings/Title";

type SummaryProps = {
  setSelectedOwner(ownerName?: string): void;
  geometry: OwnershipIntersectableGeometry;
  selectedTitle: TitleWithOwnerNames;
};

export const Summary = ({ setSelectedOwner, geometry, selectedTitle }: SummaryProps) => {
  const { t } = useTranslation();

  const selectedTitleAddresses = selectedTitle.addresses?.map(formatAddress);

  return (
    <div className="flex flex-col gap-4 bg-white" data-testid="assessment-ownership-summary">
      <div className="flex flex-col gap-3 px-2 pt-3">
        <div className="flex flex-col gap-2">
          <DataPanel.Grid>
            <DataPanel.Cell title={t("sidebar.owner.title")} data-testid="assessment-ownership-title">
              <span>{selectedTitle.title_no}</span>
            </DataPanel.Cell>
            <DataPanel.Cell title={t("sidebar.tenure.label")} data-testid="assessment-ownership-tenure">
              <Tenure tenure={selectedTitle.tenure} />
            </DataPanel.Cell>
          </DataPanel.Grid>
          <DataPanel.Grid columns={1}>
            <Owners owners={selectedTitle.ownerNames} setSelectedOwner={setSelectedOwner} />
          </DataPanel.Grid>
          <DataPanel.Grid columns={1}>
            <Address addresses={selectedTitleAddresses} />
          </DataPanel.Grid>
        </div>
      </div>
      <div className="px-2">
        <Divider />
      </div>
      <AllIntersectingOwnersSection
        setSelectedOwner={setSelectedOwner}
        selectedTitle={selectedTitle}
        geometry={geometry}
      />
    </div>
  );
};

type AllIntersectingOwnersSectionProps = Pick<SummaryProps, "setSelectedOwner" | "selectedTitle" | "geometry">;

function AllIntersectingOwnersSection({
  setSelectedOwner,
  selectedTitle,
  geometry,
}: AllIntersectingOwnersSectionProps) {
  const { titlesWithOwnerNames, loading } = useTitlesWithOwnerNamesQuery(geometry);

  if (!loading && !titlesWithOwnerNames?.length) return null;

  return (
    <div className="flex flex-col" data-testid="assessment-ownership-intersecting-owners">
      <div className="flex items-center gap-1 px-2">
        <span className="block m-0 text-xs text-blue-grey-950/50 wrap-break-word">All intersecting owners</span>
      </div>

      {loading ? (
        <div className="relative h-[46px] m-2" data-testid="ownership-related-owners-header-loading">
          <SkeletonLoading rows={1} showPadding={false} />
        </div>
      ) : (
        <RelatedOwners
          titlesWithOwnerNames={titlesWithOwnerNames}
          selectedTitleNo={selectedTitle.title_no}
          setSelectedOwner={setSelectedOwner}
        />
      )}
    </div>
  );
}

interface OwnersProps {
  owners?: string[];
  setSelectedOwner(ownerName: string): void;
}

function Owners({ owners, setSelectedOwner }: OwnersProps) {
  const { t } = useTranslation();

  if (!owners?.length)
    return (
      <DataPanel.Cell title={t("sidebar.owner.label")} variant="ghost">
        UNKNOWN
      </DataPanel.Cell>
    );

  const isSingleOwner = owners.length === 1;

  const ownersCopyString = owners.map((owner) => `${owner}\n`).join("");

  return (
    <DataPanel.Cell
      title={t("sidebar.owner.label")}
      titleTail={!isSingleOwner ? <DataPanel.Tails.Badge>{owners.length}</DataPanel.Tails.Badge> : undefined}
      variant="ghost"
      copyString={ownersCopyString}
      data-testid="assessment-ownership-owners"
    >
      <div className="flex flex-col">
        {owners.map((owner) => (
          <Tooltip.Wrapper key={owner}>
            <Tooltip.Trigger>
              <DataPanel.Button onClick={() => setSelectedOwner(owner)} data-testid="tooltip-trigger">
                <span
                  className={cn({
                    "line-clamp-2": isSingleOwner,
                    "line-clamp-1": !isSingleOwner,
                  })}
                >
                  {owner}
                </span>
              </DataPanel.Button>
            </Tooltip.Trigger>
            <Tooltip.Content sideOffset={0} showArrow={true} theme={Theme.Dark} align="start" side="bottom">
              <div>{owner}</div>
            </Tooltip.Content>
          </Tooltip.Wrapper>
        ))}
      </div>
    </DataPanel.Cell>
  );
}

interface AddressProps {
  addresses?: string[];
}

function Address({ addresses }: AddressProps) {
  if (!addresses?.length)
    return (
      <DataPanel.Cell title="Address" variant="ghost" data-testid="assessment-ownership-address">
        UNKNOWN
      </DataPanel.Cell>
    );

  const address = addresses.length === 1 ? addresses[0] : addresses.join(", ");

  return (
    <DataPanel.Cell title="Address" variant="ghost" copyString={address} data-testid="assessment-ownership-address">
      <Tooltip.Wrapper>
        <Tooltip.Trigger>
          <div className="float-left cursor-pointer line-clamp-2" data-testid="tooltip-trigger">
            {address}
          </div>
        </Tooltip.Trigger>

        <Tooltip.Content sideOffset={0} showArrow={true} theme={Theme.Dark} align="start" side="bottom">
          <div className="flex flex-col gap-1">
            {addresses.map((address) => (
              <div key={address}>{address}</div>
            ))}
          </div>
        </Tooltip.Content>
      </Tooltip.Wrapper>
    </DataPanel.Cell>
  );
}

interface TenureProps {
  tenure?: Title["tenure"];
}

function Tenure({ tenure }: TenureProps) {
  const { t, checkKey } = useTranslation();

  const value =
    tenure && checkKey(`sidebar.tenure.label.${tenure.toLocaleLowerCase()}`)
      ? t(`sidebar.tenure.label.${tenure.toLocaleLowerCase()}`)
      : t(`sidebar.tenure.label.unknown`);

  return value;
}
